class FostrCarouselButton extends HTMLElement {
  constructor() {
    super();
    this.targetEl = null;
    this.infinite = null;
    this.currentIndex = 0;
    this.previousButton = null;
    this.nextButton = null;
  }

  connectedCallback() {
    this.targetEl = document.getElementById(this.dataset.targetElId);
    if (!this.targetEl) return;
    this.infinite = this.dataset.infinite;

    // No early return on an empty target: the XGen carousels hold a spinner until their
    // cards arrive, and the cart drawer re-renders them, so the listeners have to be wired
    // up regardless — each handler re-reads the slides it needs.

    this.previousButton = this.parentNode.querySelector(
      '[data-action="previous"]'
    );
    this.nextButton = this.parentNode.querySelector('[data-action="next"]');
    this.addEventListener("click", this.handleClick);
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.handleClick(e);
      }
    });
    this.updateButtonState();
    this.targetEl.addEventListener("scroll", this.handleScroll);

    // Force an initial update after a short delay to ensure proper calculation
    setTimeout(() => {
      this.updateAriaHidden();
    }, 100);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    if (this.targetEl) {
      this.targetEl.removeEventListener("scroll", this.handleScroll);
    }
  }

  // Visible slides only: some carousels keep a hidden first child, and the grid can also
  // hold a placeholder (spinner) before its cards are injected.
  getSlides() {
    return Array.from(this.targetEl.children).filter(
      (el) => el.offsetWidth > 0
    );
  }

  // Where a scroll actually comes to rest: the left edge of the scrollport, shifted by
  // scroll-padding-left. That padding is what broke absolute scrolling here — the sliders
  // set `scroll-padding-left` to at least 5rem (see --desktop-margin-left-first-item) with
  // `scroll-snap-type: x mandatory`, so scrolling to a slide's offsetLeft got re-aligned by
  // that padding right afterwards, which on narrow carousels (cart drawer, ~110px cards) is
  // most of a card: the carousel moved once and then kept re-picking the same destination.
  // Measuring against this origin and scrolling by the difference lands exactly on the
  // slide's snap position, so a click always moves by exactly one slide.
  getSnapOrigin() {
    const styles = getComputedStyle(this.targetEl);
    const padding = parseFloat(styles.scrollPaddingLeft) || 0;
    const border = parseFloat(styles.borderLeftWidth) || 0;

    return this.targetEl.getBoundingClientRect().left + border + padding;
  }

  // Index of the slide currently resting at the start of the scrollport. Measured from live
  // rects rather than scrollLeft / slideWidth: the slides are separated by a gap, so that
  // division drifted further off with every card and ended up pointing past the last slide
  // once the carousel reached the end.
  getCurrentIndex() {
    const slides = this.getSlides();
    if (!slides.length) return 0;

    const origin = this.getSnapOrigin();
    let currentIndex = 0;
    let closest = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - origin);
      if (distance < closest) {
        closest = distance;
        currentIndex = index;
      }
    });

    return currentIndex;
  }

  handleClick = (e) => {
    e.preventDefault();
    const { action } = this.dataset;
    const slides = this.getSlides();
    if (!slides.length) return;

    // Tolerance so the slide already resting at the start is never picked as destination.
    const TOLERANCE = 2;
    const origin = this.getSnapOrigin();
    const deltas = slides.map(
      (slide) => slide.getBoundingClientRect().left - origin
    );
    const maxScroll = this.targetEl.scrollWidth - this.targetEl.clientWidth;
    const { scrollLeft } = this.targetEl;

    let nextIndex = -1;
    if (action === "next") {
      if (scrollLeft < maxScroll - 1) {
        nextIndex = deltas.findIndex((delta) => delta > TOLERANCE);
      }
      if (nextIndex === -1 && this.infinite === "true") {
        this.targetEl.appendChild(this.targetEl.children[0]);
      }
    } else if (action === "previous") {
      if (scrollLeft > 1) {
        for (let i = deltas.length - 1; i >= 0; i--) {
          if (deltas[i] < -TOLERANCE) {
            nextIndex = i;
            break;
          }
        }
      }
      if (nextIndex === -1 && this.infinite === "true") {
        this.targetEl.prepend(
          this.targetEl.children[this.targetEl.childElementCount - 1]
        );
      }
    }

    if (nextIndex === -1) return;

    this.targetEl.scrollBy({
      left: deltas[nextIndex],
      behavior: "smooth",
    });

    this.currentIndex = nextIndex;
    this.updateAriaHidden(); // Update aria-hidden attributes
  };

  handleScroll = () => {
    // Remove the timeout to update immediately
    this.currentIndex = this.getCurrentIndex();
    this.updateButtonState();
    this.updateAriaHidden();
  };

  // Mirrors exactly what handleClick would find, so an enabled arrow always moves and a
  // disabled one never had anywhere to go. The old check used a 100px slack against
  // scrollWidth, which greyed "next" out a full card early on narrow carousels (cart
  // drawer) — part of what read as the carousel freezing. Slides are laid out in order, so
  // only the outermost ones need measuring.
  updateButtonState() {
    const slides = this.getSlides();
    if (!slides.length) return;

    const TOLERANCE = 2;
    const origin = this.getSnapOrigin();
    const firstDelta = slides[0].getBoundingClientRect().left - origin;
    const lastDelta =
      slides[slides.length - 1].getBoundingClientRect().left - origin;
    const maxScroll = this.targetEl.scrollWidth - this.targetEl.clientWidth;
    const { scrollLeft } = this.targetEl;

    const isPreviousDisabled = scrollLeft <= 1 || firstDelta >= -TOLERANCE;
    const isNextDisabled =
      scrollLeft >= maxScroll - 1 || lastDelta <= TOLERANCE;

    this.previousButton.setAttribute("aria-disabled", isPreviousDisabled);
    this.nextButton.setAttribute("aria-disabled", isNextDisabled);

    if (isPreviousDisabled) {
      this.previousButton.classList.add("is-disabled");
    } else {
      this.previousButton.classList.remove("is-disabled");
    }

    if (isNextDisabled) {
      this.nextButton.classList.add("is-disabled");
    } else {
      this.nextButton.classList.remove("is-disabled");
    }
  }

  updateAriaHidden() {
    const slides = this.targetEl.children;
    const focusableSelectors =
      'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
    const containerBounds = {
      left: this.targetEl.scrollLeft,
      right: this.targetEl.scrollLeft + this.targetEl.offsetWidth,
      threshold: 10, // Minimum visible pixels to consider slide visible
    };

    Array.from(slides).forEach((slide) => {
      const slideBounds = {
        left: slide.offsetLeft,
        right: slide.offsetLeft + slide.offsetWidth,
      };

      // Modified visibility check with more lenient right boundary
      const visibleWidth =
        Math.min(
          containerBounds.right + containerBounds.threshold,
          slideBounds.right
        ) -
        Math.max(
          containerBounds.left - containerBounds.threshold,
          slideBounds.left
        );

      const isVisible = visibleWidth > slide.offsetWidth * 0.5;

      // Update slide and its focusable elements
      if (isVisible) {
        slide.setAttribute("aria-hidden", "false");
        slide.classList.add("active");
        slide.removeAttribute("tabindex");

        // Make focusable elements interactive
        slide
          .querySelectorAll(focusableSelectors)
          .forEach((el) => el.removeAttribute("tabindex"));
      } else {
        slide.setAttribute("aria-hidden", "true");
        slide.classList.remove("active");
        slide.setAttribute("tabindex", "-1");

        // Make focusable elements non-interactive
        slide
          .querySelectorAll(focusableSelectors)
          .forEach((el) => el.setAttribute("tabindex", "-1"));
      }
    });
  }
}

customElements.define("fostr-carousel-button", FostrCarouselButton);
